import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeSlash, Lock, ShieldCheck } from '@phosphor-icons/react';
import { login } from '../../utils/api';
import { useAuthStore } from '../../store';
import { toast } from 'sonner';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login: setAuth, isAuthenticated } = useAuthStore();

  const [credentials, setCredentials] = useState({ username: '', password: '', two_factor_code: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await login(credentials);

      // Check if 2FA is required
      if (res.status === 200 && res.data.error === '2FA required') {
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      const { access_token, user } = res.data.data;

      setAuth(user, access_token);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.error || error.response?.data?.detail || 'Invalid credentials';
      toast.error(message);

      // Reset 2FA if login fails
      if (requires2FA) {
        setCredentials({ ...credentials, two_factor_code: '' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-subtle flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl">
            <span className="text-primary">Luster</span>
            <span className="text-secondary"> Adorn</span>
          </h1>
          <p className="font-body text-sm text-text-secondary mt-2">Admin Panel</p>
        </div>

        {/* Login Card */}
        <div className="bg-white p-8 shadow-lg">
          <div className="flex items-center justify-center w-16 h-16 bg-primary/10 mx-auto mb-6">
            <Lock size={28} className="text-primary" weight="light" />
          </div>

          <h2 className="font-display text-2xl text-secondary text-center mb-2">
            {requires2FA ? 'Two-Factor Authentication' : 'Welcome Back'}
          </h2>
          <p className="font-body text-sm text-text-secondary text-center mb-8">
            {requires2FA
              ? 'Enter the code from your authenticator app'
              : 'Sign in to access the admin dashboard'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!requires2FA ? (
              <>
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    value={credentials.username}
                    onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                    required
                    data-testid="login-username"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body transition-colors"
                    placeholder="Enter your username"
                  />
                </div>

                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={credentials.password}
                      onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                      required
                      data-testid="login-password"
                      className="w-full px-4 py-3 pr-12 border border-border-default focus:border-primary focus:outline-none font-body transition-colors"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="block font-body text-sm text-text-secondary mb-2 flex items-center gap-2">
                  <ShieldCheck size={18} className="text-primary" />
                  Authentication Code
                </label>
                <input
                  type="text"
                  value={credentials.two_factor_code}
                  onChange={(e) => setCredentials({ ...credentials, two_factor_code: e.target.value })}
                  required
                  maxLength={6}
                  data-testid="login-2fa-code"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body transition-colors text-center text-2xl tracking-widest"
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
                <p className="font-body text-xs text-text-muted mt-2">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="btn-primary w-full disabled:opacity-50"
            >
              {loading ? 'Signing in...' : requires2FA ? 'Verify' : 'Sign In'}
            </button>

            {requires2FA && (
              <button
                type="button"
                onClick={() => {
                  setRequires2FA(false);
                  setCredentials({ ...credentials, two_factor_code: '' });
                }}
                className="w-full text-center text-sm text-text-secondary hover:text-primary transition-colors"
              >
                ← Back to login
              </button>
            )}
          </form>
        </div>

        {/* Back to store link */}
        <p className="text-center mt-6">
          <a
            href="/"
            className="font-body text-sm text-text-secondary hover:text-primary transition-colors"
          >
            ← Back to Store
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
